/* game-room-frontend/src/pages/DuelArena.jsx */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";
const DEFAULT_PLAYER_ONE_COLOR = "#2563eb";
const DEFAULT_PLAYER_TWO_COLOR = "#ef4444";
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const LOGIN_BACKGROUND_IMAGE = "url('/images/login-bg.jpg')";
const SOUND_STORAGE_KEY = "duel-sound-enabled";
const ACTION_COOLDOWN_MS = 650;

const formatDuration = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  return [hours, minutes, secs]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

const formatShortDuration = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);

  if (hours > 0) return `${hours}h${String(minutes).padStart(2, "0")}`;
  return `${minutes}min`;
};

const normalizeColor = (value, fallback) => {
  const color = String(value || "").trim();
  return HEX_COLOR_PATTERN.test(color) ? color.toLowerCase() : fallback;
};

const getPlayerName = (duel, slug) => {
  if (!duel || !slug) return "Aucun leader";
  if (duel.playerOne?.slug === slug) return duel.playerOne.name;
  if (duel.playerTwo?.slug === slug) return duel.playerTwo.name;
  return "Aucun leader";
};

const getPlayerColor = (duel, slug) => {
  if (!duel || !slug) return "#64748b";
  if (duel.playerOne?.slug === slug) {
    return normalizeColor(duel.playerOne.color, DEFAULT_PLAYER_ONE_COLOR);
  }
  if (duel.playerTwo?.slug === slug) {
    return normalizeColor(duel.playerTwo.color, DEFAULT_PLAYER_TWO_COLOR);
  }
  return "#64748b";
};

const getDuelStyle = (duel) => ({
  "--duel-player-one": normalizeColor(duel?.playerOne?.color, DEFAULT_PLAYER_ONE_COLOR),
  "--duel-player-two": normalizeColor(duel?.playerTwo?.color, DEFAULT_PLAYER_TWO_COLOR),
  "--game-room-login-bg": LOGIN_BACKGROUND_IMAGE,
});

const safePercent = (value, total) => {
  if (!total) return 50;
  return Math.max(6, Math.min(94, Math.round((Number(value || 0) / Number(total || 1)) * 100)));
};

const readSavedKey = (duelId) => localStorage.getItem(`duel-key-${duelId}`) || "";
const saveKey = (duelId, value) => localStorage.setItem(`duel-key-${duelId}`, value || "");
const forgetKey = (duelId) => localStorage.removeItem(`duel-key-${duelId}`);
const pendingKey = (dayId) => `duel-pending-actions-${dayId}`;
const draftKey = (dayId) => `duel-live-draft-${dayId}`;

const getPendingActions = (dayId) => {
  try {
    return JSON.parse(localStorage.getItem(pendingKey(dayId)) || "[]");
  } catch (error) {
    return [];
  }
};

const setPendingActions = (dayId, actions) => {
  localStorage.setItem(pendingKey(dayId), JSON.stringify(actions || []));
};

const getDraftDay = (dayId) => {
  try {
    return JSON.parse(localStorage.getItem(draftKey(dayId)) || "null");
  } catch (error) {
    return null;
  }
};

const setDraftDay = (day) => {
  if (day?._id) {
    localStorage.setItem(draftKey(day._id), JSON.stringify(day));
  }
};

const clearDraftDay = (dayId) => {
  localStorage.removeItem(draftKey(dayId));
};

const SOUND_PROFILES = {
  tap: [220, 320],
  win: [420, 660, 880],
  draw: [210, 240, 210],
  undo: [320, 180],
  success: [520, 780, 1040],
  error: [180, 140],
};

const playDuelSound = (type = "tap") => {
  if (typeof window === "undefined") return;

  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  try {
    const context = new AudioContext();
    const frequencies = SOUND_PROFILES[type] || SOUND_PROFILES.tap;
    const now = context.currentTime;

    frequencies.forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const startAt = now + index * 0.055;
      const stopAt = startAt + 0.08;

      oscillator.type = type === "draw" ? "triangle" : "sine";
      oscillator.frequency.setValueAtTime(frequency, startAt);
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(type === "error" ? 0.055 : 0.09, startAt + 0.016);
      gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(startAt);
      oscillator.stop(stopAt);
    });

    setTimeout(() => context.close().catch(() => null), 480);
  } catch (error) {
    // Les sons ne sont jamais bloquants pour le score.
  }
};

const vibrate = (pattern = 20) => {
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    navigator.vibrate(pattern);
  }
};

const getActionFeedbackLabel = (duel, day, type, winnerSlug, points) => {
  if (type === "draw") return "Match nul - le prochain but vaut plus";
  if (type === "undo") return "Derniere action annulee";
  return `${getPlayerName(duel, winnerSlug)} +${points || day?.pendingPoints || 1}`;
};

const applyLocalAction = (day, actionType, winnerSlug = null) => {
  if (!day) return day;

  const before = {
    scoreOne: Number(day.scoreOne || 0),
    scoreTwo: Number(day.scoreTwo || 0),
    pendingPoints: Number(day.pendingPoints || 1),
    drawCount: Number(day.drawCount || 0),
    realMatchCount: Number(day.realMatchCount || 0),
  };

  const localEvent = {
    _id: `local-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type: actionType,
    winnerSlug,
    points: 0,
    scoreOneBefore: before.scoreOne,
    scoreTwoBefore: before.scoreTwo,
    pendingBefore: before.pendingPoints,
    drawCountBefore: before.drawCount,
    realMatchCountBefore: before.realMatchCount,
    createdAt: new Date().toISOString(),
    isLocal: true,
  };

  const next = {
    ...day,
    events: [...(day.events || [])],
    hasLocalDraft: true,
  };

  if (actionType === "draw") {
    next.pendingPoints = before.pendingPoints + 1;
    next.drawCount = before.drawCount + 1;
    next.realMatchCount = before.realMatchCount + 1;
  }

  if (actionType === "win") {
    const points = before.pendingPoints;
    localEvent.points = points;

    if (winnerSlug === day.playerOneSlug) {
      next.scoreOne = before.scoreOne + points;
      next.scoreTwo = before.scoreTwo;
    } else {
      next.scoreOne = before.scoreOne;
      next.scoreTwo = before.scoreTwo + points;
    }

    next.pendingPoints = 1;
    next.drawCount = before.drawCount;
    next.realMatchCount = before.realMatchCount + 1;
  }

  localEvent.scoreOneAfter = next.scoreOne || 0;
  localEvent.scoreTwoAfter = next.scoreTwo || 0;
  localEvent.pendingAfter = next.pendingPoints || 1;
  localEvent.drawCountAfter = next.drawCount || 0;
  localEvent.realMatchCountAfter = next.realMatchCount || 0;
  next.events.push(localEvent);

  return next;
};

const undoLocalAction = (day) => {
  if (!day?.events?.length) return day;

  const events = [...day.events];
  const last = events.pop();

  return {
    ...day,
    scoreOne: last.scoreOneBefore || 0,
    scoreTwo: last.scoreTwoBefore || 0,
    pendingPoints: last.pendingBefore || 1,
    drawCount: last.drawCountBefore || 0,
    realMatchCount: last.realMatchCountBefore || 0,
    events,
    hasLocalDraft: true,
  };
};

const getDayDateValue = (day) => new Date(day?.displayDate || day?.endedAt || day?.startedAt || Date.now()).getTime();

const getSortedHistory = (duel) => [...(duel?.history || [])].sort((a, b) => getDayDateValue(b) - getDayDateValue(a));

const getWinnerLabel = (duel, day) => day?.winnerSlug ? getPlayerName(duel, day.winnerSlug) : "Egalite";

const getRecentForm = (duel, limit = 5) => {
  const days = getSortedHistory(duel).slice(0, limit).reverse();
  return days.map((day) => {
    if (!day.winnerSlug) return { label: "N", winnerSlug: null, day };
    return {
      label: day.winnerSlug === duel.playerOne.slug ? "V" : "D",
      winnerSlug: day.winnerSlug,
      day,
    };
  });
};

const getCurrentStreak = (duel) => {
  const days = getSortedHistory(duel);
  if (!days.length || !days[0].winnerSlug) return { winnerSlug: null, count: 0 };

  const winnerSlug = days[0].winnerSlug;
  let count = 0;
  for (const day of days) {
    if (day.winnerSlug !== winnerSlug) break;
    count += 1;
  }
  return { winnerSlug, count };
};

const getBestStreak = (duel) => {
  const days = [...(duel?.history || [])].sort((a, b) => getDayDateValue(a) - getDayDateValue(b));
  let best = { winnerSlug: null, count: 0 };
  let current = { winnerSlug: null, count: 0 };

  days.forEach((day) => {
    if (!day.winnerSlug) {
      current = { winnerSlug: null, count: 0 };
      return;
    }

    if (current.winnerSlug === day.winnerSlug) {
      current.count += 1;
    } else {
      current = { winnerSlug: day.winnerSlug, count: 1 };
    }

    if (current.count > best.count) {
      best = { ...current };
    }
  });

  return best;
};

const getRecords = (duel) => {
  const history = duel?.history || [];
  const initial = {
    biggestGap: null,
    longestDay: null,
    mostPoints: null,
  };

  return history.reduce((acc, day) => {
    const gap = Math.abs(Number(day.scoreOne || 0) - Number(day.scoreTwo || 0));
    const total = Number(day.scoreOne || 0) + Number(day.scoreTwo || 0);
    const duration = Number(day.durationSeconds || 0);

    if (!acc.biggestGap || gap > acc.biggestGap.gap) {
      acc.biggestGap = { day, gap };
    }
    if (!acc.longestDay || duration > acc.longestDay.duration) {
      acc.longestDay = { day, duration };
    }
    if (!acc.mostPoints || total > acc.mostPoints.total) {
      acc.mostPoints = { day, total };
    }

    return acc;
  }, initial);
};

const getSeasonStats = (duel) => {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();

  const days = (duel?.history || []).filter((day) => {
    const date = new Date(day.displayDate || day.endedAt || day.startedAt);
    return date.getMonth() === month && date.getFullYear() === year;
  });

  return days.reduce((acc, day) => {
    acc.days += 1;
    acc.pointsOne += Number(day.scoreOne || 0);
    acc.pointsTwo += Number(day.scoreTwo || 0);
    if (day.winnerSlug === duel.playerOne.slug) acc.winsOne += 1;
    if (day.winnerSlug === duel.playerTwo.slug) acc.winsTwo += 1;
    return acc;
  }, { days: 0, winsOne: 0, winsTwo: 0, pointsOne: 0, pointsTwo: 0 });
};

const getPlayerRanking = (duels) => {
  const players = new Map();

  duels.forEach((duel) => {
    const stats = duel.stats || {};
    [duel.playerOne, duel.playerTwo].forEach((player) => {
      if (!players.has(player.slug)) {
        players.set(player.slug, {
          slug: player.slug,
          name: player.name,
          color: normalizeColor(player.color, player.slug === duel.playerOne.slug ? DEFAULT_PLAYER_ONE_COLOR : DEFAULT_PLAYER_TWO_COLOR),
          dayWins: 0,
          points: 0,
          duels: 0,
        });
      }
    });

    const one = players.get(duel.playerOne.slug);
    const two = players.get(duel.playerTwo.slug);
    one.dayWins += Number(stats.playerOneDayWins || 0);
    one.points += Number(stats.playerOnePoints || 0);
    one.duels += 1;
    two.dayWins += Number(stats.playerTwoDayWins || 0);
    two.points += Number(stats.playerTwoPoints || 0);
    two.duels += 1;
  });

  return Array.from(players.values()).sort((a, b) => b.dayWins - a.dayWins || b.points - a.points || a.name.localeCompare(b.name));
};

const ScoreboardLine = ({
  label,
  leftName,
  rightName,
  leftScore,
  rightScore,
  leftColor,
  rightColor,
  subLabel,
  leaderSide,
  compact = false,
}) => (
  <section className={`duel-foot-scoreboard ${compact ? "duel-foot-scoreboard-compact" : ""}`}>
    <div className={`duel-foot-team duel-foot-team-left ${leaderSide === "left" ? "is-leading" : ""}`}>
      <span className="duel-team-stripe" style={{ backgroundColor: leftColor }} />
      <strong>{leftName}</strong>
      <small>{leaderSide === "left" ? "Leader" : ""}</small>
    </div>

    <div className="duel-foot-center">
      <span>{label}</span>
      <strong>
        <b style={{ color: leftColor }}>{leftScore}</b>
        <em>-</em>
        <b style={{ color: rightColor }}>{rightScore}</b>
      </strong>
      <small>{subLabel}</small>
    </div>

    <div className={`duel-foot-team duel-foot-team-right ${leaderSide === "right" ? "is-leading" : ""}`}>
      <span className="duel-team-stripe" style={{ backgroundColor: rightColor }} />
      <strong>{rightName}</strong>
      <small>{leaderSide === "right" ? "Leader" : ""}</small>
    </div>
  </section>
);

const DuelArena = ({ isHomePage = false, tvMode = false }) => {
  const { duelId } = useParams();
  const navigate = useNavigate();
  const wakeLockRef = useRef(null);
  const actionLockedUntilRef = useRef(0);

  const [duels, setDuels] = useState([]);
  const [duel, setDuel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [managementKey, setManagementKey] = useState(() => (duelId ? readSavedKey(duelId) : ""));
  const [showMatchDetails, setShowMatchDetails] = useState(true);
  const [liveFocus, setLiveFocus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [actionFeedback, setActionFeedback] = useState(null);
  const [actionPulse, setActionPulse] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [confirmEndOpen, setConfirmEndOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem(SOUND_STORAGE_KEY) !== "false");
  const [createForm, setCreateForm] = useState({
    playerOneName: "",
    playerTwoName: "",
    game: "FC 25",
    playerOneColor: DEFAULT_PLAYER_ONE_COLOR,
    playerTwoColor: DEFAULT_PLAYER_TWO_COLOR,
  });
  const [importForm, setImportForm] = useState({
    displayDate: new Date().toISOString().slice(0, 10),
    scoreOne: "",
    scoreTwo: "",
    realMatchCount: "",
    drawCount: "0",
    note: "",
  });

  useEffect(() => {
    document.documentElement.style.setProperty("--game-room-login-bg", LOGIN_BACKGROUND_IMAGE);
  }, []);

  const playFeedbackSound = useCallback((type, vibrationPattern = 20) => {
    if (soundEnabled) playDuelSound(type);
    vibrate(vibrationPattern);
  }, [soundEnabled]);

  const apiRequest = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || "Une erreur est survenue");
    }
    return data;
  }, []);

  const mergeLocalDraft = useCallback((duelData) => {
    if (!duelData?.activeDay?._id) return duelData;

    const pending = getPendingActions(duelData.activeDay._id);
    const draft = getDraftDay(duelData.activeDay._id);

    if (draft && pending.length > 0) {
      return {
        ...duelData,
        activeDay: {
          ...draft,
          pendingSyncCount: pending.length,
          hasLocalDraft: true,
        },
      };
    }

    return duelData;
  }, []);

  const fetchDuels = useCallback(async () => {
    const data = await apiRequest("/api/duels");
    setDuels(data);
  }, [apiRequest]);

  const fetchDuel = useCallback(async () => {
    if (!duelId) return;
    const data = await apiRequest(`/api/duels/${duelId}`);
    setDuel(mergeLocalDraft(data));
  }, [apiRequest, duelId, mergeLocalDraft]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        if (duelId) {
          setManagementKey(readSavedKey(duelId));
          await fetchDuel();
        } else {
          setLiveFocus(false);
          await fetchDuels();
        }
      } catch (err) {
        setError(err.message || "Impossible de charger les duels pour le moment.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [duelId, fetchDuel, fetchDuels]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const activeDay = duel?.activeDay || null;

  useEffect(() => {
    let cancelled = false;

    const requestWakeLock = async () => {
      if (!activeDay || activeDay.status !== "active") return;
      if (!("wakeLock" in navigator)) return;

      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch (err) {
        console.warn("Wake Lock non disponible :", err.message);
      }
    };

    const releaseWakeLock = async () => {
      try {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
      } catch (err) {
        console.warn("Impossible de liberer Wake Lock :", err.message);
      }
    };

    const handleVisibility = () => {
      if (!cancelled && document.visibilityState === "visible") {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      releaseWakeLock();
    };
  }, [activeDay]);

  const pendingActions = activeDay?._id ? getPendingActions(activeDay._id) : [];
  const duelStyle = getDuelStyle(duel);
  const stats = duel?.stats || {};
  const leftColor = getPlayerColor(duel, duel?.playerOne?.slug);
  const rightColor = getPlayerColor(duel, duel?.playerTwo?.slug);
  const totalDayWins = Number(stats.playerOneDayWins || 0) + Number(stats.playerTwoDayWins || 0);
  const totalPoints = Number(stats.playerOnePoints || 0) + Number(stats.playerTwoPoints || 0);
  const dayPercent = safePercent(stats.playerOneDayWins || 0, totalDayWins);
  const pointPercent = safePercent(stats.playerOnePoints || 0, totalPoints);
  const leaderByDays = stats.leaderByDays;
  const leaderByPoints = stats.leaderByPoints;
  const recentForm = useMemo(() => getRecentForm(duel, 5), [duel]);
  const currentStreak = useMemo(() => getCurrentStreak(duel), [duel]);
  const bestStreak = useMemo(() => getBestStreak(duel), [duel]);
  const records = useMemo(() => getRecords(duel), [duel]);
  const seasonStats = useMemo(() => getSeasonStats(duel), [duel]);
  const sortedHistory = useMemo(() => getSortedHistory(duel), [duel]);
  const latestDay = sortedHistory[0] || null;
  const playerRanking = useMemo(() => getPlayerRanking(duels), [duels]);
  const featuredDuel = useMemo(() => {
    if (!duels.length) return null;
    return [...duels].sort((a, b) => {
      const aActive = a.activeDay ? 1 : 0;
      const bActive = b.activeDay ? 1 : 0;
      const aTotal = Number(a.stats?.totalDays || 0) + Number(a.stats?.totalPoints || 0);
      const bTotal = Number(b.stats?.totalDays || 0) + Number(b.stats?.totalPoints || 0);
      return bActive - aActive || bTotal - aTotal;
    })[0];
  }, [duels]);

  const liveDuration = useMemo(() => {
    if (!activeDay?.startedAt) return 0;
    if (activeDay.status === "completed") return activeDay.durationSeconds || 0;
    return Math.floor((now - new Date(activeDay.startedAt).getTime()) / 1000);
  }, [activeDay, now]);

  useEffect(() => {
    if (!activeDay) {
      setLiveFocus(false);
      setConfirmEndOpen(false);
    }
  }, [activeDay]);

  const setActiveDay = (day) => {
    setDuel((current) => (current ? { ...current, activeDay: day } : current));
  };

  const refreshCurrentDuel = useCallback(async () => {
    if (duelId) {
      const data = await apiRequest(`/api/duels/${duelId}`);
      setDuel(mergeLocalDraft(data));
    }
  }, [apiRequest, duelId, mergeLocalDraft]);

  const canRunAction = useCallback(() => {
    const time = Date.now();
    if (busy || time < actionLockedUntilRef.current) return false;
    actionLockedUntilRef.current = time + ACTION_COOLDOWN_MS;
    return true;
  }, [busy]);

  const triggerActionFeedback = useCallback((type, winnerSlug = null, points = 1) => {
    const label = getActionFeedbackLabel(duel, activeDay, type, winnerSlug, points);
    const color = type === "draw" ? "#d97706" : getPlayerColor(duel, winnerSlug);

    setActionPulse(type === "draw" ? "draw" : winnerSlug);
    setActionFeedback({
      id: Date.now(),
      type,
      winnerSlug,
      points,
      label,
      color,
    });

    window.setTimeout(() => setActionPulse(null), 520);
    window.setTimeout(() => setActionFeedback(null), 1250);
  }, [activeDay, duel]);

  const handleSaveKey = () => {
    if (!duelId) return;
    saveKey(duelId, managementKey.trim());
    playFeedbackSound("tap", 12);
    setSuccess("Code de gestion enregistre sur ce telephone.");
  };

  const handleForgetKey = () => {
    if (!duelId) return;
    forgetKey(duelId);
    setManagementKey("");
    playFeedbackSound("undo", 12);
    setSuccess("Code oublie sur cet appareil.");
  };

  const handleToggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem(SOUND_STORAGE_KEY, String(next));
    if (next) playDuelSound("tap");
  };

  const copyText = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      playFeedbackSound("success", 18);
      setSuccess(message);
    } catch (err) {
      setError("Impossible de copier automatiquement. Copie le lien manuellement.");
    }
  };

  const sharePublicDuel = async () => {
    const url = window.location.href.replace(/\/tv$/, "");
    if (navigator.share) {
      try {
        await navigator.share({ title: duel?.title || "Duel Infinity Game", url });
        return;
      } catch (err) {
        // Si l'utilisateur annule le partage natif, on propose la copie.
      }
    }
    await copyText(url, "Lien public du duel copie.");
  };

  const copyManagementKey = async () => {
    if (!managementKey.trim()) return;
    await copyText(managementKey.trim(), "Code de gestion copie.");
  };

  const handleCreateDuel = async (event) => {
    event.preventDefault();
    if (!canRunAction()) return;
    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const created = await apiRequest("/api/duels", {
        method: "POST",
        body: JSON.stringify(createForm),
      });

      saveKey(created._id, created.managementKey);
      playFeedbackSound("success", [20, 35, 20]);
      setSuccess(`Duel cree. Code de gestion : ${created.managementKey}. Garde-le pour modifier le duel depuis un autre telephone.`);
      navigate(`/duels/${created._id}`);
    } catch (err) {
      playFeedbackSound("error", [20, 30, 20]);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startDay = async () => {
    if (!duelId || !canRunAction()) return;
    setBusy(true);
    setError("");
    setSuccess("");

    try {
      saveKey(duelId, managementKey.trim());
      await apiRequest(`/api/duels/${duelId}/start`, {
        method: "POST",
        body: JSON.stringify({ managementKey: managementKey.trim() }),
      });
      playFeedbackSound("success", [25, 45, 25]);
      setSuccess("La journee est lancee.");
      setLiveFocus(true);
      await refreshCurrentDuel();
    } catch (err) {
      playFeedbackSound("error", [20, 30, 20]);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const syncPendingActions = useCallback(async () => {
    if (!activeDay?._id || !managementKey.trim()) return;

    const actions = getPendingActions(activeDay._id);
    if (actions.length === 0) return;

    setBusy(true);
    setError("");
    try {
      let lastDay = activeDay;
      for (const action of actions) {
        const path = action.type === "draw" ? "draw" : "win";
        const payload = action.type === "draw"
          ? { managementKey: managementKey.trim() }
          : { managementKey: managementKey.trim(), winnerSlug: action.winnerSlug };

        const data = await apiRequest(`/api/duels/days/${activeDay._id}/${path}`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        lastDay = data.day;
      }

      setPendingActions(activeDay._id, []);
      clearDraftDay(activeDay._id);
      setActiveDay(lastDay);
      playFeedbackSound("success", [15, 25, 15]);
      setSuccess("Actions hors ligne synchronisees.");
      await refreshCurrentDuel();
    } catch (err) {
      playFeedbackSound("error", [20, 30, 20]);
      setError(`Synchronisation impossible : ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [activeDay, apiRequest, managementKey, playFeedbackSound, refreshCurrentDuel]);

  useEffect(() => {
    const handleOnline = () => syncPendingActions();
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [syncPendingActions]);

  const recordAction = async (type, winnerSlug = null) => {
    if (!activeDay?._id || !canRunAction()) return;
    setBusy(true);
    setError("");
    setSuccess("");

    const path = type === "draw" ? "draw" : "win";
    const payload = type === "draw"
      ? { managementKey: managementKey.trim() }
      : { managementKey: managementKey.trim(), winnerSlug };

    try {
      const data = await apiRequest(`/api/duels/days/${activeDay._id}/${path}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const lastEvent = data.day?.events?.[data.day.events.length - 1];
      playFeedbackSound(type === "draw" ? "draw" : "win", type === "draw" ? [18, 18] : 22);
      triggerActionFeedback(type, winnerSlug, lastEvent?.points || activeDay.pendingPoints || 1);
      setActiveDay(data.day);
      clearDraftDay(activeDay._id);
      setPendingActions(activeDay._id, []);
      await refreshCurrentDuel();
    } catch (err) {
      if (navigator.onLine) {
        playFeedbackSound("error", [20, 30, 20]);
        setError(err.message);
      } else {
        const localDay = applyLocalAction(activeDay, type, winnerSlug);
        const actions = getPendingActions(activeDay._id);
        setPendingActions(activeDay._id, [
          ...actions,
          { type, winnerSlug, createdAt: new Date().toISOString() },
        ]);
        setDraftDay(localDay);
        playFeedbackSound(type === "draw" ? "draw" : "win", 20);
        triggerActionFeedback(type, winnerSlug, activeDay.pendingPoints || 1);
        setActiveDay({ ...localDay, pendingSyncCount: actions.length + 1 });
        setError("Connexion coupee : action gardee en memoire sur ce telephone.");
      }
    } finally {
      setBusy(false);
    }
  };

  const undoLastAction = async () => {
    if (!activeDay?._id || !canRunAction()) return;
    setBusy(true);
    setError("");
    setSuccess("");

    const actions = getPendingActions(activeDay._id);
    if (actions.length > 0) {
      const nextActions = actions.slice(0, -1);
      const nextDay = undoLocalAction(activeDay);
      setPendingActions(activeDay._id, nextActions);
      setDraftDay(nextDay);
      setActiveDay({ ...nextDay, pendingSyncCount: nextActions.length });
      playFeedbackSound("undo", 18);
      triggerActionFeedback("undo", null, 0);
      setBusy(false);
      setSuccess("Derniere action locale annulee.");
      return;
    }

    try {
      const data = await apiRequest(`/api/duels/days/${activeDay._id}/undo`, {
        method: "POST",
        body: JSON.stringify({ managementKey: managementKey.trim() }),
      });
      playFeedbackSound("undo", 18);
      triggerActionFeedback("undo", null, 0);
      setActiveDay(data.day);
      await refreshCurrentDuel();
    } catch (err) {
      playFeedbackSound("error", [20, 30, 20]);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const endDay = async () => {
    if (!activeDay?._id || !canRunAction()) return;

    if (getPendingActions(activeDay._id).length > 0) {
      setError("Synchronise d'abord les actions en attente avant de terminer la journee.");
      return;
    }

    setBusy(true);
    setError("");
    setSuccess("");

    try {
      await apiRequest(`/api/duels/days/${activeDay._id}/end`, {
        method: "POST",
        body: JSON.stringify({ managementKey: managementKey.trim() }),
      });
      clearDraftDay(activeDay._id);
      playFeedbackSound("success", [25, 40, 25]);
      setLiveFocus(false);
      setConfirmEndOpen(false);
      setSuccess("Journee terminee et ajoutee aux statistiques.");
      await refreshCurrentDuel();
    } catch (err) {
      playFeedbackSound("error", [20, 30, 20]);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const importOldDay = async (event) => {
    event.preventDefault();
    if (!duelId || !canRunAction()) return;

    setBusy(true);
    setError("");
    setSuccess("");

    try {
      await apiRequest(`/api/duels/${duelId}/import-day`, {
        method: "POST",
        body: JSON.stringify({
          ...importForm,
          managementKey: managementKey.trim(),
        }),
      });
      setImportForm({
        displayDate: new Date().toISOString().slice(0, 10),
        scoreOne: "",
        scoreTwo: "",
        realMatchCount: "",
        drawCount: "0",
        note: "",
      });
      playFeedbackSound("success", [20, 30, 20]);
      setShowImportForm(false);
      setSuccess("Ancienne journee ajoutee aux statistiques.");
      await refreshCurrentDuel();
    } catch (err) {
      playFeedbackSound("error", [20, 30, 20]);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const renderRecentEvents = (events, limit = 8) => {
    const shownEvents = (events || []).slice().reverse().slice(0, limit);

    if (!shownEvents.length) {
      return <div className="duel-events-empty">Aucune action pour le moment.</div>;
    }

    return (
      <div className="duel-events-list">
        {shownEvents.map((event) => (
          <div key={event._id} className="duel-event-item">
            <span>
              {event.type === "draw"
                ? "Match nul"
                : event.type === "undo"
                  ? "Action annulee"
                  : `${getPlayerName(duel, event.winnerSlug)} +${event.points}`}
            </span>
            <small>{new Date(event.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</small>
          </div>
        ))}
      </div>
    );
  };

  const renderDuelCard = (item, featured = false) => {
    const itemStats = item.stats || {};
    const itemLeftColor = getPlayerColor(item, item.playerOne.slug);
    const itemRightColor = getPlayerColor(item, item.playerTwo.slug);
    const itemLeaderByDays = itemStats.leaderByDays;
    const itemLeaderSide = itemLeaderByDays === item.playerOne.slug ? "left" : itemLeaderByDays === item.playerTwo.slug ? "right" : null;
    const itemTotalDayWins = Number(itemStats.playerOneDayWins || 0) + Number(itemStats.playerTwoDayWins || 0);
    const itemTotalPoints = Number(itemStats.playerOnePoints || 0) + Number(itemStats.playerTwoPoints || 0);
    const itemDayPercent = safePercent(itemStats.playerOneDayWins || 0, itemTotalDayWins);
    const itemPointPercent = safePercent(itemStats.playerOnePoints || 0, itemTotalPoints);
    const lastDay = getSortedHistory(item)[0];

    return (
      <Link to={`/duels/${item._id}`} className={`duel-match-card ${featured ? "duel-match-card-featured" : ""}`} style={getDuelStyle(item)} key={item._id}>
        <div className="duel-match-card-header">
          <span>{item.game}</span>
          {item.activeDay ? <strong>LIVE</strong> : <small>{itemStats.totalDays || 0} journees</small>}
        </div>

        <ScoreboardLine
          label="Journees"
          leftName={item.playerOne.name}
          rightName={item.playerTwo.name}
          leftScore={itemStats.playerOneDayWins || 0}
          rightScore={itemStats.playerTwoDayWins || 0}
          leftColor={itemLeftColor}
          rightColor={itemRightColor}
          subLabel={`${itemTotalDayWins} terminee${itemTotalDayWins > 1 ? "s" : ""}`}
          leaderSide={itemLeaderSide}
          compact
        />

        <div className="duel-match-card-bars">
          <div className="duel-sport-bar"><span style={{ width: `${itemDayPercent}%` }} /></div>
          <div className="duel-card-points-line">
            <span>{itemStats.playerOnePoints || 0}</span>
            <small>{itemTotalPoints} points</small>
            <span>{itemStats.playerTwoPoints || 0}</span>
          </div>
          <div className="duel-sport-bar duel-sport-bar-points"><span style={{ width: `${itemPointPercent}%` }} /></div>
        </div>

        {lastDay && (
          <p className="duel-last-result">
            Dernier resultat : {lastDay.playerOneName} {lastDay.scoreOne} - {lastDay.scoreTwo} {lastDay.playerTwoName}
          </p>
        )}
      </Link>
    );
  };

  const renderFormBadges = () => (
    <div className="duel-form-strip">
      {recentForm.length ? recentForm.map((form, index) => (
        <span
          key={`${form.day?._id || index}-${index}`}
          className={form.winnerSlug ? "is-win" : "is-draw"}
          style={{ borderColor: form.winnerSlug ? getPlayerColor(duel, form.winnerSlug) : "#94a3b8" }}
          title={form.day ? `${form.day.playerOneName} ${form.day.scoreOne} - ${form.day.scoreTwo} ${form.day.playerTwoName}` : ""}
        >
          {form.winnerSlug ? getPlayerName(duel, form.winnerSlug).slice(0, 1).toUpperCase() : "N"}
        </span>
      )) : <small>Aucune forme recente.</small>}
    </div>
  );

  const renderLiveControls = (isFocusMode = false) => {
    if (!activeDay) return null;

    const leaderSide = Number(activeDay.scoreOne || 0) > Number(activeDay.scoreTwo || 0)
      ? "left"
      : Number(activeDay.scoreTwo || 0) > Number(activeDay.scoreOne || 0)
        ? "right"
        : null;
    const lastEvent = activeDay.events?.length ? activeDay.events[activeDay.events.length - 1] : null;

    return (
      <section className={`${isFocusMode ? "duel-live-matchday" : "duel-live-panel"} ${actionPulse ? "duel-action-pulse" : ""}`}>
        <div className="duel-live-score-top">
          <div>
            <span className="duel-kicker">Journee en cours</span>
            <h2>{duel.game}</h2>
          </div>
          <div className="duel-live-clock" aria-label="Chrono">
            <small>Chrono</small>
            <strong>{formatDuration(liveDuration)}</strong>
          </div>
          <button type="button" onClick={handleToggleSound} className="duel-icon-button">
            {soundEnabled ? "Son ON" : "Son OFF"}
          </button>
        </div>

        {actionFeedback && (
          <div key={actionFeedback.id} className={`duel-click-feedback duel-click-feedback-${actionFeedback.type}`} style={{ "--feedback-color": actionFeedback.color }}>
            <strong>{actionFeedback.label}</strong>
            <span>{actionFeedback.type === "draw" ? "Bonus reporte" : actionFeedback.type === "undo" ? "Correction appliquee" : "Score mis a jour"}</span>
          </div>
        )}

        <ScoreboardLine
          label="Score du jour"
          leftName={activeDay.playerOneName}
          rightName={activeDay.playerTwoName}
          leftScore={activeDay.scoreOne || 0}
          rightScore={activeDay.scoreTwo || 0}
          leftColor={leftColor}
          rightColor={rightColor}
          subLabel={`${activeDay.realMatchCount || 0} matchs joues - prochain +${activeDay.pendingPoints || 1}`}
          leaderSide={leaderSide}
        />

        <div className="duel-live-status-row">
          <span>{navigator.onLine ? "En ligne" : "Hors ligne"}</span>
          <span>{activeDay.pendingSyncCount || pendingActions.length || 0} action(s) a synchroniser</span>
          <span>{activeDay.drawCount || 0} nul(s)</span>
        </div>

        <div className="duel-live-buttons-grid">
          <button
            type="button"
            disabled={busy}
            onClick={() => recordAction("win", activeDay.playerOneSlug)}
            className={`duel-score-action duel-score-action-left ${actionPulse === activeDay.playerOneSlug ? "duel-button-hit" : ""}`}
          >
            <span>{activeDay.playerOneName}</span>
            <strong>+{activeDay.pendingPoints || 1}</strong>
            <small>Victoire</small>
          </button>

          <button type="button" disabled={busy} onClick={() => recordAction("draw")} className={`duel-score-action duel-score-action-draw ${actionPulse === "draw" ? "duel-button-hit" : ""}`}>
            <span>Match nul</span>
            <strong>x{(activeDay.pendingPoints || 1) + 1}</strong>
            <small>Prochain match</small>
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() => recordAction("win", activeDay.playerTwoSlug)}
            className={`duel-score-action duel-score-action-right ${actionPulse === activeDay.playerTwoSlug ? "duel-button-hit" : ""}`}
          >
            <span>{activeDay.playerTwoName}</span>
            <strong>+{activeDay.pendingPoints || 1}</strong>
            <small>Victoire</small>
          </button>
        </div>

        <div className="duel-live-actions">
          {!isFocusMode && <button type="button" onClick={() => setLiveFocus(true)} disabled={busy}>Grand ecran</button>}
          <button type="button" onClick={syncPendingActions} disabled={busy || pendingActions.length === 0}>Synchroniser</button>
          <button type="button" onClick={undoLastAction} disabled={busy || !activeDay.events?.length}>Annuler</button>
          <button type="button" onClick={() => setConfirmEndOpen(true)} disabled={busy} className="duel-danger-button">Terminer</button>
        </div>

        {lastEvent && (
          <div className="duel-last-action-box">
            <span>Derniere action</span>
            <strong>{lastEvent.type === "draw" ? "Match nul" : `${getPlayerName(duel, lastEvent.winnerSlug)} +${lastEvent.points}`}</strong>
          </div>
        )}

        {renderRecentEvents(activeDay.events, isFocusMode ? 5 : 8)}
      </section>
    );
  };

  const renderEndConfirmation = () => {
    if (!confirmEndOpen || !activeDay) return null;

    const winner = Number(activeDay.scoreOne || 0) > Number(activeDay.scoreTwo || 0)
      ? activeDay.playerOneName
      : Number(activeDay.scoreTwo || 0) > Number(activeDay.scoreOne || 0)
        ? activeDay.playerTwoName
        : "Egalite";

    return (
      <div className="duel-modal-layer" role="dialog" aria-modal="true">
        <div className="duel-modal-card duel-themed" style={duelStyle}>
          <p className="duel-kicker">Confirmation</p>
          <h2>Terminer cette journee ?</h2>
          <ScoreboardLine
            label="Fin de journee"
            leftName={activeDay.playerOneName}
            rightName={activeDay.playerTwoName}
            leftScore={activeDay.scoreOne || 0}
            rightScore={activeDay.scoreTwo || 0}
            leftColor={leftColor}
            rightColor={rightColor}
            subLabel={`Vainqueur : ${winner}`}
            leaderSide={winner === activeDay.playerOneName ? "left" : winner === activeDay.playerTwoName ? "right" : null}
            compact
          />
          <div className="duel-confirm-stats">
            <span>{formatDuration(liveDuration)}</span>
            <span>{activeDay.realMatchCount || 0} matchs</span>
            <span>{activeDay.drawCount || 0} nuls</span>
          </div>
          <div className="duel-modal-actions">
            <button type="button" onClick={() => setConfirmEndOpen(false)}>Continuer</button>
            <button type="button" onClick={endDay} className="duel-danger-button" disabled={busy}>Confirmer la fin</button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="duel-page"><div className="duel-loading">Chargement des duels...</div></div>;
  }

  if (!duelId) {
    return (
      <main className="duel-page duel-home-page">
        <section className="duel-top-nav">
          <Link to="/" className="duel-brand-link">Infinity Game Duels</Link>
          <Link to="/login" className="duel-login-link">Connexion equipe</Link>
        </section>

        <section className="duel-home-hero-clean">
          <div>
            <p className="duel-kicker">Tableau public</p>
            <h1>Les rivalites de la salle</h1>
            <p>
              Scores de journees, points de matchs, forme actuelle et resultats recents dans un affichage inspire du football.
            </p>
          </div>
          <button type="button" onClick={() => setShowCreateForm((value) => !value)} className="duel-primary-button">
            {showCreateForm ? "Fermer" : "Creer un duel"}
          </button>
        </section>

        {error && <div className="duel-alert duel-alert-error">{error}</div>}
        {success && <div className="duel-alert duel-alert-success">{success}</div>}

        {featuredDuel && (
          <section className="duel-featured-section">
            <div className="duel-section-heading">
              <p className="duel-kicker">Affiche du moment</p>
              <h2>Rivalite principale</h2>
            </div>
            {renderDuelCard(featuredDuel, true)}
          </section>
        )}

        {duels.length > 0 && (
          <section className="duel-ranking-panel">
            <div className="duel-section-heading">
              <p className="duel-kicker">Classement general</p>
              <h2>Joueurs</h2>
            </div>
            <div className="duel-ranking-list">
              {playerRanking.slice(0, 5).map((player, index) => (
                <div key={player.slug} className="duel-ranking-row">
                  <strong>#{index + 1}</strong>
                  <span className="duel-ranking-dot" style={{ backgroundColor: player.color }} />
                  <span>{player.name}</span>
                  <small>{player.dayWins} journees - {player.points} points</small>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="duel-grid duel-home-duels">
          {duels.length > 0 ? duels.map((item) => renderDuelCard(item)) : (
            <div className="duel-empty-card">
              <h2>Aucun duel pour le moment</h2>
              <p>Cree ta premiere rivalite FC 25, par exemple Loic contre Yosri.</p>
              <button type="button" onClick={() => setShowCreateForm(true)} className="duel-primary-button">Creer le premier duel</button>
            </div>
          )}
        </section>

        {showCreateForm && (
          <section className="duel-panel duel-create-panel">
            <div>
              <p className="duel-kicker">Creation</p>
              <h2>Nouvelle affiche de duel</h2>
              <p>Choisis les joueurs, le jeu et deux couleurs sobres. Les couleurs servent d'accent sportif, pas de decoration excessive.</p>
            </div>

            <div className="duel-create-preview duel-themed" style={{
              "--duel-player-one": normalizeColor(createForm.playerOneColor, DEFAULT_PLAYER_ONE_COLOR),
              "--duel-player-two": normalizeColor(createForm.playerTwoColor, DEFAULT_PLAYER_TWO_COLOR),
            }}>
              <ScoreboardLine
                label={createForm.game || "FC 25"}
                leftName={createForm.playerOneName || "Joueur gauche"}
                rightName={createForm.playerTwoName || "Joueur droit"}
                leftScore={0}
                rightScore={0}
                leftColor={normalizeColor(createForm.playerOneColor, DEFAULT_PLAYER_ONE_COLOR)}
                rightColor={normalizeColor(createForm.playerTwoColor, DEFAULT_PLAYER_TWO_COLOR)}
                subLabel="Apercu de l'affiche"
                compact
              />
            </div>

            <form onSubmit={handleCreateDuel} className="duel-form-grid">
              <input
                value={createForm.playerOneName}
                onChange={(event) => setCreateForm({ ...createForm, playerOneName: event.target.value })}
                placeholder="Joueur gauche"
                required
              />
              <input
                value={createForm.playerTwoName}
                onChange={(event) => setCreateForm({ ...createForm, playerTwoName: event.target.value })}
                placeholder="Joueur droit"
                required
              />
              <input
                value={createForm.game}
                onChange={(event) => setCreateForm({ ...createForm, game: event.target.value })}
                placeholder="Jeu"
              />
              <div className="duel-color-fields">
                <label>
                  Couleur joueur gauche
                  <input
                    type="color"
                    value={createForm.playerOneColor}
                    onChange={(event) => setCreateForm({ ...createForm, playerOneColor: event.target.value })}
                  />
                </label>
                <label>
                  Couleur joueur droit
                  <input
                    type="color"
                    value={createForm.playerTwoColor}
                    onChange={(event) => setCreateForm({ ...createForm, playerTwoColor: event.target.value })}
                  />
                </label>
              </div>
              <button type="submit" disabled={busy}>Creer le duel</button>
            </form>
          </section>
        )}
      </main>
    );
  }

  if (!duel) {
    return (
      <main className="duel-page">
        <Link to="/duels" className="duel-back-link">Retour aux duels</Link>
        <div className="duel-empty-card">Duel introuvable.</div>
      </main>
    );
  }

  const leaderSideDays = leaderByDays === duel.playerOne.slug ? "left" : leaderByDays === duel.playerTwo.slug ? "right" : null;
  const leaderSidePoints = leaderByPoints === duel.playerOne.slug ? "left" : leaderByPoints === duel.playerTwo.slug ? "right" : null;

  if (tvMode) {
    return (
      <main className="duel-tv-page duel-themed" style={duelStyle}>
        <div className="duel-tv-top">
          <span>{duel.game}</span>
          <strong>{activeDay ? "LIVE" : "TABLEAU PUBLIC"}</strong>
        </div>
        <ScoreboardLine
          label={activeDay ? "Score du jour" : "Journees"}
          leftName={duel.playerOne.name}
          rightName={duel.playerTwo.name}
          leftScore={activeDay ? activeDay.scoreOne || 0 : stats.playerOneDayWins || 0}
          rightScore={activeDay ? activeDay.scoreTwo || 0 : stats.playerTwoDayWins || 0}
          leftColor={leftColor}
          rightColor={rightColor}
          subLabel={activeDay ? `${formatDuration(liveDuration)} - prochain +${activeDay.pendingPoints || 1}` : `${stats.totalDays || 0} journees terminees`}
          leaderSide={activeDay ? (Number(activeDay.scoreOne || 0) > Number(activeDay.scoreTwo || 0) ? "left" : Number(activeDay.scoreTwo || 0) > Number(activeDay.scoreOne || 0) ? "right" : null) : leaderSideDays}
        />
        <div className="duel-tv-meta">
          <span>Points totaux : {stats.playerOnePoints || 0} - {stats.playerTwoPoints || 0}</span>
          <span>Matchs reels : {stats.realMatchCount || 0}</span>
          <span>Nuls : {stats.drawCount || 0}</span>
        </div>
      </main>
    );
  }

  if (activeDay && liveFocus) {
    return (
      <main className="duel-live-focus-page duel-themed" style={duelStyle}>
        <div className="duel-live-focus-top">
          <button type="button" onClick={() => setLiveFocus(false)} className="duel-secondary-button">Retour stats</button>
          <span>{duel.game}</span>
          <button type="button" onClick={handleToggleSound} className="duel-secondary-button">{soundEnabled ? "Son ON" : "Son OFF"}</button>
        </div>

        {error && <div className="duel-alert duel-alert-error">{error}</div>}
        {success && <div className="duel-alert duel-alert-success">{success}</div>}

        <section className="duel-live-focus-title">
          <p>Match day</p>
          <h1>{duel.playerOne.name} <span>vs</span> {duel.playerTwo.name}</h1>
        </section>

        {renderLiveControls(true)}
        {renderEndConfirmation()}
      </main>
    );
  }

  return (
    <main className="duel-page duel-detail-page duel-themed" style={duelStyle}>
      <section className="duel-top-nav">
        <Link to="/duels" className="duel-back-link">Retour aux duels</Link>
        <div className="duel-detail-actions">
          <button type="button" onClick={sharePublicDuel} className="duel-secondary-button">Partager</button>
          <Link to={`/duels/${duel._id}/tv`} className="duel-secondary-link">Mode TV</Link>
          <button type="button" onClick={handleToggleSound} className="duel-secondary-button">{soundEnabled ? "Son ON" : "Son OFF"}</button>
        </div>
      </section>

      <section className="duel-detail-hero-clean">
        <div>
          <p className="duel-kicker">{duel.game}</p>
          <h1>{duel.playerOne.name} <span>vs</span> {duel.playerTwo.name}</h1>
          <p>
            Leader des journees : {leaderByDays ? getPlayerName(duel, leaderByDays) : "egalite"}. Leader aux points : {leaderByPoints ? getPlayerName(duel, leaderByPoints) : "egalite"}.
          </p>
        </div>
        {latestDay && (
          <div className="duel-latest-result-card">
            <span>Derniere journee</span>
            <strong>{latestDay.scoreOne} - {latestDay.scoreTwo}</strong>
            <small>{getWinnerLabel(duel, latestDay)}</small>
          </div>
        )}
      </section>

      {error && <div className="duel-alert duel-alert-error">{error}</div>}
      {success && <div className="duel-alert duel-alert-success">{success}</div>}

      <ScoreboardLine
        label="Journees"
        leftName={duel.playerOne.name}
        rightName={duel.playerTwo.name}
        leftScore={stats.playerOneDayWins || 0}
        rightScore={stats.playerTwoDayWins || 0}
        leftColor={leftColor}
        rightColor={rightColor}
        subLabel={`${stats.totalDays || 0} journees terminees`}
        leaderSide={leaderSideDays}
      />

      <div className="duel-large-bar">
        <span>{duel.playerOne.name}</span>
        <div className="duel-sport-bar"><span style={{ width: `${dayPercent}%` }} /></div>
        <span>{duel.playerTwo.name}</span>
      </div>

      <section className="duel-toggle-row">
        <button type="button" onClick={() => setShowMatchDetails((value) => !value)} className="duel-secondary-button">
          {showMatchDetails ? "Masquer les stats matchs" : "Voir les stats matchs"}
        </button>
        <span>{stats.realMatchCount || 0} matchs reels - {stats.drawCount || 0} nuls - {stats.pointsGap || 0} points d'ecart</span>
      </section>

      {showMatchDetails && (
        <section className="duel-panel duel-stats-panel-clean">
          <ScoreboardLine
            label="Points de matchs"
            leftName={duel.playerOne.name}
            rightName={duel.playerTwo.name}
            leftScore={stats.playerOnePoints || 0}
            rightScore={stats.playerTwoPoints || 0}
            leftColor={leftColor}
            rightColor={rightColor}
            subLabel={`${stats.totalPoints || 0} points comptabilises`}
            leaderSide={leaderSidePoints}
            compact
          />
          <div className="duel-large-bar duel-match-bar">
            <span>{duel.playerOne.name}</span>
            <div className="duel-sport-bar duel-sport-bar-points"><span style={{ width: `${pointPercent}%` }} /></div>
            <span>{duel.playerTwo.name}</span>
          </div>
          <div className="duel-mini-stats">
            <span>{stats.realMatchCount || 0} matchs reellement joues</span>
            <span>{stats.drawCount || 0} matchs nuls reportes</span>
            <span>{stats.pointsGap || 0} points d'ecart</span>
          </div>
        </section>
      )}

      <section className="duel-panel duel-insights-panel">
        <div className="duel-section-heading">
          <p className="duel-kicker">Dynamique</p>
          <h2>Forme, series et records</h2>
        </div>
        <div className="duel-insight-grid">
          <article>
            <span>Forme recente</span>
            {renderFormBadges()}
          </article>
          <article>
            <span>Serie actuelle</span>
            <strong>{currentStreak.count > 0 ? `${getPlayerName(duel, currentStreak.winnerSlug)} x${currentStreak.count}` : "Aucune"}</strong>
          </article>
          <article>
            <span>Meilleure serie</span>
            <strong>{bestStreak.count > 0 ? `${getPlayerName(duel, bestStreak.winnerSlug)} x${bestStreak.count}` : "Aucune"}</strong>
          </article>
          <article>
            <span>Plus grosse victoire</span>
            <strong>{records.biggestGap?.day ? `${records.biggestGap.day.scoreOne} - ${records.biggestGap.day.scoreTwo}` : "Aucune"}</strong>
          </article>
          <article>
            <span>Journee la plus longue</span>
            <strong>{records.longestDay?.duration ? formatShortDuration(records.longestDay.duration) : "Aucune"}</strong>
          </article>
          <article>
            <span>Saison actuelle</span>
            <strong>{seasonStats.winsOne} - {seasonStats.winsTwo}</strong>
          </article>
        </div>
      </section>

      <section className="duel-panel duel-management-panel">
        <div>
          <p className="duel-kicker">Gestion du duel</p>
          <h2>Commencer, corriger ou importer</h2>
          <p>La page reste publique. Le code sert seulement a modifier le duel depuis cet appareil.</p>
        </div>
        <div className="duel-key-row">
          <input
            value={managementKey}
            onChange={(event) => setManagementKey(event.target.value)}
            placeholder="Code de gestion du duel"
            type="password"
          />
          <button type="button" onClick={handleSaveKey}>Enregistrer</button>
          <button type="button" onClick={copyManagementKey} disabled={!managementKey.trim()}>Copier</button>
          <button type="button" onClick={handleForgetKey} disabled={!managementKey.trim()}>Oublier</button>
        </div>
      </section>

      {activeDay ? (
        renderLiveControls(false)
      ) : (
        <section className="duel-panel duel-start-panel">
          <div>
            <p className="duel-kicker">Nouvelle journee</p>
            <h2>Aucune journee en cours</h2>
            <p>Le chrono est enregistre cote serveur et reprend meme apres coupure, batterie faible ou fermeture du navigateur.</p>
          </div>
          <button type="button" onClick={startDay} disabled={busy || !managementKey.trim()} className="duel-primary-button">
            Commencer la journee
          </button>
        </section>
      )}

      <section className="duel-panel duel-import-panel">
        <div className="duel-panel-title-row">
          <div>
            <p className="duel-kicker">Historique manuel</p>
            <h2>Ajouter une ancienne journee</h2>
            <p>Chaque ajout est marque comme manuel afin de rester transparent dans l'historique.</p>
          </div>
          <button type="button" onClick={() => setShowImportForm((value) => !value)} className="duel-secondary-button">
            {showImportForm ? "Fermer" : "Ajouter"}
          </button>
        </div>

        {showImportForm && (
          <form onSubmit={importOldDay} className="duel-import-grid">
            <label>
              Date
              <input
                type="date"
                value={importForm.displayDate}
                onChange={(event) => setImportForm({ ...importForm, displayDate: event.target.value })}
                required
              />
            </label>
            <label>
              Score {duel.playerOne.name}
              <input
                type="number"
                min="0"
                value={importForm.scoreOne}
                onChange={(event) => setImportForm({ ...importForm, scoreOne: event.target.value })}
                required
              />
            </label>
            <label>
              Score {duel.playerTwo.name}
              <input
                type="number"
                min="0"
                value={importForm.scoreTwo}
                onChange={(event) => setImportForm({ ...importForm, scoreTwo: event.target.value })}
                required
              />
            </label>
            <label>
              Matchs reels
              <input
                type="number"
                min="0"
                value={importForm.realMatchCount}
                onChange={(event) => setImportForm({ ...importForm, realMatchCount: event.target.value })}
                placeholder="Optionnel"
              />
            </label>
            <label>
              Nuls
              <input
                type="number"
                min="0"
                value={importForm.drawCount}
                onChange={(event) => setImportForm({ ...importForm, drawCount: event.target.value })}
              />
            </label>
            <label className="duel-import-note">
              Note
              <input
                value={importForm.note}
                onChange={(event) => setImportForm({ ...importForm, note: event.target.value })}
                placeholder="Ex : journee ajoutee depuis l'ancien carnet"
              />
            </label>
            <div className="duel-manual-warning">Cette journee sera indiquee comme ajout manuel.</div>
            <button type="submit" disabled={busy || !managementKey.trim()}>Ajouter a l'historique</button>
          </form>
        )}
      </section>

      <section className="duel-history-section">
        <div className="duel-section-heading">
          <p className="duel-kicker">Historique</p>
          <h2>Journees enregistrees</h2>
        </div>

        <div className="duel-history-list">
          {sortedHistory.length ? sortedHistory.map((day) => {
            const winner = getWinnerLabel(duel, day);
            return (
              <details key={day._id} className="duel-history-card">
                <summary>
                  <span className="duel-history-date">{day.formattedDate}</span>
                  <strong>{day.playerOneName} {day.scoreOne} - {day.scoreTwo} {day.playerTwoName}</strong>
                  <small>{winner} - {day.source === "manual" ? "Manuel" : "Live"}</small>
                </summary>
                <div className="duel-history-meta">
                  <span>{day.realMatchCount || day.scoreOne + day.scoreTwo} matchs</span>
                  <span>{formatDuration(day.durationSeconds || 0)}</span>
                  <span>{day.drawCount || 0} nuls</span>
                  {day.note && <span>{day.note}</span>}
                </div>
                {renderRecentEvents(day.events, 12)}
              </details>
            );
          }) : <div className="duel-empty-card">Aucune journee terminee pour ce duel.</div>}
        </div>
      </section>

      {renderEndConfirmation()}
    </main>
  );
};

export default DuelArena;
